// Username validation utility for CrapperMapper
// Prevents explicit content and enforces username rules

const USERNAME_RULES = {
  maxLength: 30,
  minLength: 4,
  allowedChars: /^[a-zA-Z0-9_-]+$/, // Letters, numbers, underscore, hyphen
  blockedWords: [
    // Common profanity (basic list - expand as needed)
    'fuck', 'shit', 'ass', 'bitch', 'bastard', 'hell',
    'piss', 'dick', 'cock', 'pussy', 'tit', 'cunt',
    'whore', 'slut', 'nigga', 'nigger', 'fucker', 'fucking', 
    'shithead', 'asshole',
    
    // Variations and creative spellings
    'fuk', 'a$$', 'b1tch', 'd1ck', 'c0ck', 'pu$$y', 'n1gga',
    'fck', 'dumbass', 'motherfucker',
       
    // Brand/impersonation protection
    'admin', 'administrator', 'crappermapper', 'support', 'moderator',
    'official', 'verified', 'system', 'root', 'api', 'bot'
  ],
  reservedNames: [
    'admin', 'administrator', 'crappermapper', 'support', 'moderator',
    'official', 'verified', 'system', 'root', 'api', 'bot', 'help',
    'info', 'contact', 'team', 'staff', 'security', 'abuse'
  ]
};

/**
 * Enhanced username validation with profanity filter and length limits
 * @param {string} username - Username to validate
 * @returns {Object} - Validation result with isValid, error, and details
 */
export const validateUsername = (username) => {
  // Empty username is valid for initial typing state
  if (!username || username.trim().length === 0) {
    return { isValid: true, error: '', details: { type: 'empty' } };
  }

  const trimmedUsername = username.trim().toLowerCase();
  
  // Length validation
  if (trimmedUsername.length < USERNAME_RULES.minLength) {
    return {
      isValid: false,
      error: `Username must be at least ${USERNAME_RULES.minLength} characters`,
      details: { type: 'too_short', minLength: USERNAME_RULES.minLength }
    };
  }

  if (trimmedUsername.length > USERNAME_RULES.maxLength) {
    return {
      isValid: false,
      error: `Username must be ${USERNAME_RULES.maxLength} characters or less`,
      details: { type: 'too_long', maxLength: USERNAME_RULES.maxLength }
    };
  }

  // Character validation
  if (!USERNAME_RULES.allowedChars.test(trimmedUsername)) {
    return {
      isValid: false,
      error: 'Username can only contain letters, numbers, underscores, and hyphens',
      details: { type: 'invalid_chars' }
    };
  }

  // Reserved names check
  if (USERNAME_RULES.reservedNames.includes(trimmedUsername)) {
    return {
      isValid: false,
      error: 'This username is not available',
      details: { type: 'reserved' }
    };
  }

  // Blocked words/profanity check
  const containsBlockedWord = USERNAME_RULES.blockedWords.some(word => {
    // Check for exact match or partial match
    return trimmedUsername.includes(word) || 
           word.includes(trimmedUsername) ||
           // Check for leet speak variations
           checkLeetSpeak(trimmedUsername, word);
  });

  if (containsBlockedWord) {
    return {
      isValid: false,
      error: 'This username is not available',
      details: { type: 'blocked_content' }
    };
  }

  // Check for suspicious patterns (multiple special chars, etc.)
  const specialCharCount = (trimmedUsername.match(/[_-]/g) || []).length;
  if (specialCharCount > trimmedUsername.length / 2) {
    return {
      isValid: false,
      error: 'This username is not available',
      details: { type: 'too_many_special_chars' }
    };
  }

  // All checks passed
  return {
    isValid: true,
    error: '',
    details: { type: 'valid' }
  };
};

/**
 * Check for leet speak variations of blocked words
 * @param {string} username - Username to check
 * @param {string} blockedWord - Blocked word to check against
 * @returns {boolean} - True if leet speak variation detected
 */
const checkLeetSpeak = (username, blockedWord) => {
  const leetMap = {
    'a': ['4', '@'],
    'e': ['3'],
    'i': ['1', '!'],
    'o': ['0'],
    's': ['5', '$'],
    't': ['7'],
    'l': ['1'],
    'g': ['6', '9']
  };

  // Create variations of the blocked word with leet speak
  let variations = [blockedWord];
  
  for (const [letter, replacements] of Object.entries(leetMap)) {
    const newVariations = [];
    for (const variation of variations) {
      for (const replacement of replacements) {
        newVariations.push(variation.replace(new RegExp(letter, 'g'), replacement));
      }
    }
    variations.push(...newVariations);
  }

  // Check if username contains any leet speak variation
  return variations.some(variation => username.includes(variation));
};

/**
 * Get username requirements for UI display
 * @returns {Object} - Username requirements object
 */
export const getUsernameRequirements = () => {
  return {
    minLength: USERNAME_RULES.minLength,
    maxLength: USERNAME_RULES.maxLength,
    allowedChars: 'Letters, numbers, underscores, and hyphens',
    description: `${USERNAME_RULES.minLength}-${USERNAME_RULES.maxLength} characters, alphanumeric only`
  };
};

export default validateUsername;
